/** Mixpanel's exposure event. The SDK emits this from `get_variant_sync`. */
export const HOMEPAGE_EXPOSURE_EVENT = '$experiment_started';

/**
 * How long a treatment visitor waits for the exposure send before navigating
 * anyway. Mixpanel's own link-tracking timeout is 300ms; this stays inside
 * that bound so a blocked or missing callback cannot strand the redirect.
 */
export const HOMEPAGE_EXPOSURE_REDIRECT_TIMEOUT_MS = 250;

/**
 * `sendBeacon` queues the request and invokes Mixpanel's callback in the same
 * turn. Safari drops that beacon when `location.replace` runs in that same
 * turn, so the redirect waits one task for the browser to accept it.
 */
export const HOMEPAGE_EXPOSURE_REDIRECT_YIELD_MS = 0;

export type HomepageExposureTrack = (
  this: unknown,
  eventName: string,
  properties?: Record<string, unknown>,
  optionsOrCallback?: Record<string, unknown> | ((response: unknown) => void),
  callback?: (response: unknown) => void,
) => unknown;

const isTreatmentExposure = (
  eventName: string,
  properties: Record<string, unknown> | undefined,
  experimentName: string,
): boolean =>
  eventName === HOMEPAGE_EXPOSURE_EVENT &&
  properties?.['Experiment name'] === experimentName &&
  properties?.['Variant name'] === 'treatment';

/**
 * Wraps Mixpanel `track` so a homepage treatment exposure bypasses the
 * batch queue. The SDK calls `track('$experiment_started', props)` with no
 * transport, and `batch_requests` (default on in mixpanel-browser 2.72)
 * holds the event for up to 5s. Safari does not flush that queue on a
 * synchronous `location.replace`: the unload path only beacons from
 * `visibilitychange`, which Safari skips, and from `pagehide` only when the
 * page is entering the back/forward cache.
 *
 * Control exposures and every other event keep the caller's options.
 * Event name and properties are forwarded unchanged.
 */
export const createHomepageExposureTracker = (
  originalTrack: HomepageExposureTrack,
  experimentName: string,
): {
  track: HomepageExposureTrack;
  read: <T>(readFlag: () => T) => {
    value: T;
    exposureSent: Promise<void> | null;
  };
} => {
  let pendingResolve: (() => void) | null = null;
  let matched = false;

  const track: HomepageExposureTrack = function track(
    eventName,
    properties,
    optionsOrCallback,
    callback,
  ) {
    if (
      pendingResolve === null ||
      !isTreatmentExposure(eventName, properties, experimentName)
    ) {
      return originalTrack.call(
        this,
        eventName,
        properties,
        optionsOrCallback,
        callback,
      );
    }

    matched = true;
    const resolve = pendingResolve;
    pendingResolve = null;
    const options =
      typeof optionsOrCallback === 'function' || optionsOrCallback == null
        ? {}
        : optionsOrCallback;
    const userCallback =
      typeof optionsOrCallback === 'function' ? optionsOrCallback : callback;

    return originalTrack.call(
      this,
      eventName,
      properties,
      {
        ...options,
        send_immediately: true,
        transport: 'sendBeacon',
      },
      (response: unknown) => {
        resolve();
        userCallback?.(response);
      },
    );
  };

  return {
    track,
    read(readFlag) {
      matched = false;
      const exposureSent = new Promise<void>((resolve) => {
        pendingResolve = resolve;
      });
      try {
        return {
          value: readFlag(),
          exposureSent: matched ? exposureSent : null,
        };
      } finally {
        pendingResolve = null;
      }
    },
  };
};

/**
 * Navigates after a treatment exposure has been handed to the browser.
 * No in-flight exposure (control, cookie, override, deduped read) navigates
 * immediately. A send that never calls back still navigates at `timeoutMs`.
 */
export const redirectAfterHomepageExposure = ({
  redirectTo,
  exposureSent,
  replace,
  timeoutMs = HOMEPAGE_EXPOSURE_REDIRECT_TIMEOUT_MS,
}: {
  redirectTo: string;
  exposureSent: Promise<void> | null;
  replace: (url: string) => void;
  timeoutMs?: number;
}): void => {
  if (!exposureSent) {
    replace(redirectTo);
    return;
  }

  let redirected = false;
  const go = () => {
    if (redirected) return;
    redirected = true;
    replace(redirectTo);
  };

  const fallback = setTimeout(go, timeoutMs);
  const redirectAfterSend = () => {
    clearTimeout(fallback);
    setTimeout(go, HOMEPAGE_EXPOSURE_REDIRECT_YIELD_MS);
  };
  exposureSent.then(redirectAfterSend, redirectAfterSend);
};
